import assertNever from '../utils/assertNever';

import { ASTExpr, ASTProgram, stringifyExpr } from './ast';
import { Context } from './types';
import { debugAssert } from './utils';
import { Quantity } from './quantity';
import { unitToString } from '../unit';
import { typecheckExpression } from './typecheck';
import { getFunction } from './functions';
import { convertToType } from './typeConversion';

export function evalExpression(ctx: Context, expr: ASTExpr): unknown {
  switch (expr.t) {
    case 'string':
    case 'number':
    case 'boolean':
      return expr.v;
    case 'unary':
      switch (expr.op) {
        case '-': {
          const v = evalExpression(ctx, expr.expr);
          if (typeof v === 'number') {
            return -v;
          } else if (v instanceof Quantity) {
            return new Quantity(-v, v.unit);
          } else {
            throw new Error(
              `error: unknown type ${v} for unary minus. Expected Quantity or number`
            );
          }
        }
        default:
          assertNever(expr);
      }
    case 'identifier': {
      const v = ctx.variables.get(expr.name);
      debugAssert(
        v !== undefined,
        () =>
          `unknown variable ${expr.name} (know about ${[
            ...ctx.variables.keys(),
          ].join(', ')})`
      );
      return v;
    }
    case 'binary': {
      const l = assertNumber(evalExpression(ctx, expr.l));
      const r = assertNumber(evalExpression(ctx, expr.r));
      switch (expr.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          if (r === 0) {
            throw new Error(
              `error: cannot divide by zero in expression ${stringifyExpr(
                expr
              )}`
            );
          }
          return l / r;
        default:
          assertNever(expr);
      }
    }
    case 'comparison': {
      const l = assertComparable(evalExpression(ctx, expr.l));
      const r = assertComparable(evalExpression(ctx, expr.r));
      switch (expr.op) {
        case '==':
          return l === r;
        case '!=':
          return l !== r;
        case '<':
          if (l === null || r === null) {
            return null;
          }
          return l < r;
        case '<=':
          if (l === null || r === null) {
            return null;
          }
          return l <= r;
        case '>':
          if (l === null || r === null) {
            return null;
          }
          return l > r;
        case '>=':
          if (l === null || r === null) {
            return null;
          }
          return l >= r;
        default:
          assertNever(expr);
      }
    }
    case 'call': {
      const name = expr.name;
      const fn = getFunction(name);
      debugAssert(
        fn != null,
        () => `unknown function ${name} in expression ${stringifyExpr(expr)}`
      );
      return fn.eval(ctx, expr);
    }
    case 'null':
      return null;
    default:
      assertNever(expr);
  }
}

/*
 * Eval an expression that can only do math, it can't do any function calls. This lets it be used in the
 * context of a synchronous unit conversion.
 */

export function evalLimitedExpression(ctx: Context, expr: ASTExpr): unknown {
  switch (expr.t) {
    case 'number':
      return expr.v;
    case 'unary':
      switch (expr.op) {
        case '-': {
          const v = evalLimitedExpression(ctx, expr.expr);
          if (typeof v === 'number') {
            return -v;
          } else if (v instanceof Quantity) {
            return new Quantity(-v, v.unit);
          } else {
            throw new Error(
              `error: unknown type ${v} for unary minus. Expected Quantity or number`
            );
          }
        }
        default:
          assertNever(expr);
      }
    case 'identifier': {
      const v = ctx.variables.get(expr.name);
      debugAssert(
        v !== undefined,
        () =>
          `unknown variable ${expr.name} (know about ${[
            ...ctx.variables.keys(),
          ].join(', ')})`
      );
      return v;
    }
    case 'binary': {
      const [lEval, rEval] = [
        evalLimitedExpression(ctx, expr.l),
        evalLimitedExpression(ctx, expr.r),
      ];
      const l = assertNumber(lEval);
      const r = assertNumber(rEval);
      switch (expr.op) {
        case '+':
          return l + r;
        case '-':
          return l - r;
        case '*':
          return l * r;
        case '/':
          if (r === 0) {
            throw new Error(
              `error: cannot divide by zero in expression ${stringifyExpr(
                expr
              )}`
            );
          }
          return l / r;
        default:
          assertNever(expr);
      }
    }
    case 'comparison':
      throw new Error('Cannot do comparisons in limited expressions');
    case 'call':
      throw new Error('Cannot do function calls in limited expressions');
    case 'string':
      throw new Error("Can't use strings in limited expressions");
    case 'boolean':
      throw new Error("Can't use booleans in limited expressions");
    case 'null':
      throw new Error("Can't use null in limited expressions");
    default:
      assertNever(expr);
  }
}
export function evalProgram(
  ctx: Context,
  prog: ASTProgram
): Record<string, unknown> {
  ctx = {
    ...ctx,
    variables: new Map(ctx.variables),
  };
  const out: Record<string, unknown> = {};
  for (const line of prog) {
    let v: unknown;
    if (line.typeDeclaration) {
      v = convertToType(ctx, {
        fromExpr: line.expr,
        toType: line.typeDeclaration,
      }).eval();
    } else {
      v = evalExpression(ctx, line.expr);
    }

    ctx.variables.set(line.name, v);
    ctx.variablesType.set(
      line.name,
      line.typeDeclaration ?? typecheckExpression(ctx, line.expr)
    );
    out[line.name] = v;
  }
  return out;
}

export function assertString(v: unknown): string {
  if (typeof v !== 'string') {
    throw new Error(
      `internal type error: expected string, got ${v} (${typeof v})`
    );
  }
  return v;
}

export function assertNumber(v: unknown): number {
  if (typeof v === 'number') {
    return v;
  } else if (v instanceof Quantity) {
    throw new Error(
      `internal type error: expected number, got Quantity(${
        v.value
      }, ${unitToString(v.unit)})`
    );
  } else {
    throw new Error(
      `internal type error: expected number, got ${v} (${typeof v})`
    );
  }
}

export function assertComparable(v: unknown): string | number | null {
  if (typeof v !== 'number' && typeof v !== 'string' && v !== null) {
    throw new Error(
      `internal type error: expected comparable (number or string), got ${typeof v}`
    );
  }
  return v;
}

export function assertConstantValue(v: unknown): number | string | boolean {
  if (
    typeof v !== 'number' &&
    typeof v !== 'string' &&
    typeof v !== 'boolean'
  ) {
    throw new Error(
      `internal type error: expected number | string | boolean, got ${v} (${typeof v})`
    );
  }
  return v;
}
